import classnames from 'classnames';
import React from 'react';
import { FeatureToggle } from 'react-feature-toggles';
import { Role, RootState } from 'v2/models';
import { getUserRole } from 'v2/utils/user';
import styles from '../navbar.module.scss';
import { OptionType } from '../../../form/CustomSelectors/SearchesSelect';
import { hasPermission, PERMISSION_NAME } from '../../../utils/permissions';
import { NavbarDropdown } from '../../NavbarDropdown/NavbarDropdown';
import { useTranslation } from 'react-i18next';
import { PostProcessKeys, TranslationKeys } from 'translations';
import { useSelector } from 'react-redux';
import NavBarLink from './NavbarLink';
import { NavItem } from '../Navbar';

interface NavbarLinkItemProps {
  item: NavItem;
  handleExpand: (expanded: boolean) => void;
}

const NavbarLinkItem = ({ item, handleExpand }: NavbarLinkItemProps) => {
  const { t } = useTranslation();
  const user = useSelector((state: RootState) => state.user.user);
  const permissions = useSelector(
    (state: RootState) => state.permissions.permissions
  );
  const userRole: Role = getUserRole(user);

  const isRegularSearcher = hasPermission(permissions, [
    PERMISSION_NAME.CREATE_SEARCH,
    PERMISSION_NAME.CREATE_SEARCH_OWN,
  ]);
  const isFeaturedDataProviderUser = hasPermission(permissions, [
    PERMISSION_NAME.FEATURED_PROVIDERS,
  ]);
  const isReviewer = hasPermission(permissions, [
    PERMISSION_NAME.REVIEW_SEARCH_ORG,
    PERMISSION_NAME.REVIEW_SEARCH_MASTER_ORG,
  ]);
  const isSearcher = hasPermission(permissions, [
    PERMISSION_NAME.CREATE_SEARCH,
    PERMISSION_NAME.CREATE_INTERNAL_SEARCH,
    PERMISSION_NAME.CREATE_SEARCH_OWN,
    PERMISSION_NAME.CREATE_INTERNAL_SEARCH_OWN,
  ]);

  const shouldShowSearchesDropDown = () => {
    if (userRole === 'dataBuyer') {
      return (
        (isReviewer && isSearcher) || (isFeaturedDataProviderUser && isSearcher)
      );
    }
  };

  const renderDrmNavbar = (item: NavItem) => {
    if (!hasPermission(permissions, item.permissions || [])) {
      return null;
    }
    const options: OptionType[] = [
      {
        label: t(TranslationKeys.dataProvider_other, {
          postProcess: PostProcessKeys.TitleCase,
        }),
        value: 'Data Providers',
        to: '/data-providers/1',
      },
      {
        label: t(TranslationKeys.contact_other, {
          postProcess: PostProcessKeys.TitleCase,
        }),
        value: 'Contacts',
        to: `/contacts/1`,
      },
      {
        label: t(TranslationKeys.dataProduct_other, {
          postProcess: PostProcessKeys.TitleCase,
        }),
        value: 'Datasets',
        to: `/data-sets/1`,
      },
    ];
    return (
      <div
        className={classnames(
          'float-left',
          styles.navLinkLine,
          styles.navLinkTitle
        )}
        key={item.label}
      >
        <div className="float-left mr-3" data-cy="navbar-interactions">
          <NavbarDropdown
            label={item.label}
            options={options}
            onSelect={() => handleExpand(false)}
          />
        </div>
      </div>
    );
  };

  if (item.permissions && !hasPermission(permissions, item.permissions)) {
    return null;
  }

  if (item.label === 'Requests' && shouldShowSearchesDropDown()) {
    const myRequestsRoute = isRegularSearcher
      ? `/requests/requester/active/1`
      : `/requests/requester/in-progress/1`;

    const options: OptionType[] = [
      buildRequestsDropdownItem('My Requests', myRequestsRoute),
    ];

    if (isReviewer) {
      options.push(buildRequestsDropdownItem('All Requests', '/requests'));
    }

    if (isFeaturedDataProviderUser) {
      options.push(
        buildRequestsDropdownItem('New Providers', `/featured-data-providers/1`)
      );
    }

    return (
      <div
        className={classnames(
          'float-left',
          styles.navLinkTitle,
          styles.navLinkLine
        )}
        key={item.label}
      >
        <div className="float-left mr-3">
          <NavbarDropdown
            label={item.label}
            options={options}
            onSelect={() => handleExpand(false)}
          />
        </div>
      </div>
    );
  }

  if (item.key === 'interactions') {
    return renderDrmNavbar(item);
  }

  return item.featureFlag ? (
    <FeatureToggle key={item.label} featureName={item.featureFlag}>
      {<NavBarLink item={item} handleExpand={handleExpand} />}
    </FeatureToggle>
  ) : (
    <NavBarLink item={item} handleExpand={handleExpand} />
  );
};

export default NavbarLinkItem;

function buildRequestsDropdownItem(label: string, to: string): OptionType {
  return { value: label, label, to };
}
